import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Share2, Users } from 'lucide-react'
import { useAuth } from '@core/auth/useAuth'
import { useAppStore } from '@core/store'
import { abrirJanelaPartilhar } from '@core/partilhas/partilhasEstado'
import { abrirPartilharSessao, visivelParaDaSessao } from './partilharSessao'
import { useSessaoAbertaAgora } from './vigiaDaSessaoPartilhada'
import { sessoesApi, type SessaoResumo } from './sessoesApi'

const BOTAO_PEQUENO =
  'shrink-0 rounded border border-[var(--color-border)] px-1 py-0 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]'

/** As linhas da partilha no menu Sessão da barra de topo.
 *
 *  - Sessão por partilhar: "Partilhar sessão" (com o módulo dos modelos
 *    partilhados, que é onde a sessão vive).
 *  - Sessão partilhada (por mim, ou aberta de uma partilha): "Gerir
 *    partilha da sessão" e, por baixo, "Visível para: ...".
 *  - As outras sessões partilhadas deste workspace que a pessoa vê (as
 *    suas, mesmo depois de fechar a sessão, e as que lhe partilharam):
 *    abrir, ou gerir a partilha (é por aqui que se retira a partilha de
 *    uma sessão que já não está aberta).
 */
export default function MenuPartilharSessao({
  close,
  itemClassName,
}: {
  close: () => void
  itemClassName: string
}) {
  const { mode, workspace, user, modules } = useAuth()
  const navigate = useNavigate()
  const actual = useSessaoAbertaAgora(workspace?.id)
  const temAlgo = useAppStore((s) => s.models.length > 0 || s.bcfLoaded.length > 0)
  const [visivel, setVisivel] = useState<{ id: string; texto: string | null } | null>(null)
  const [outras, setOutras] = useState<SessaoResumo[]>([])
  const partilhaId = actual?.partilhaId ?? null
  const ligado = mode === 'cloud' && !!workspace && !!user
  const convidado = !!workspace?.convidado

  useEffect(() => {
    if (!partilhaId) return
    let vivo = true
    void visivelParaDaSessao(partilhaId).then((texto) => {
      if (vivo) setVisivel({ id: partilhaId, texto })
    })
    return () => {
      vivo = false
    }
  }, [partilhaId])

  useEffect(() => {
    if (!ligado || convidado) return
    let vivo = true
    void sessoesApi
      .listar()
      .then((l) => {
        if (vivo) setOutras(l)
      })
      .catch(() => {
        /* sem a lista: o resto do menu funciona */
      })
    return () => {
      vivo = false
    }
  }, [ligado, convidado])

  if (!ligado || !workspace || !user) return null
  const podePartilhar = modules.includes('modelos') && !convidado
  const lista = outras.filter((s) => s.id !== partilhaId).slice(0, 8)
  if (!actual && !podePartilhar && lista.length === 0) return null

  const ctx = {
    workspaceId: workspace.id,
    workspaceSlug: workspace.slug,
    workspaceNome: workspace.name,
    utilizadorId: user.id,
    utilizadorNome: user.name || user.email,
  }
  const abrir = () => {
    close()
    void abrirPartilharSessao(ctx)
  }

  return (
    <>
      <div className="my-1 h-px bg-[var(--color-border)]" aria-hidden />
      {actual ? (
        <>
          <button
            type="button"
            className={itemClassName}
            title={
              actual.minha
                ? 'Com quem está partilhada esta sessão: acrescentar e remover pessoas, enviar o que mudou, ou retirar a partilha.'
                : 'Com quem está partilhada esta sessão.'
            }
            onClick={abrir}
          >
            <Users size={14} />
            Gerir partilha da sessão
          </button>
          <p
            className="px-2 pb-1 text-[10px] leading-snug text-[var(--color-text-dim)]"
            data-testid="sessao-visivel-para"
          >
            {visivel?.id === partilhaId
              ? (visivel.texto ?? 'Não foi possível ler com quem está partilhada.')
              : 'A ler com quem está partilhada…'}
          </p>
        </>
      ) : (
        podePartilhar && (
          <button
            type="button"
            className={`${itemClassName} disabled:opacity-50`}
            disabled={!temAlgo}
            title={
              temAlgo
                ? 'Partilhar a sessão de uma vez: os modelos abertos, a triagem dos conflitos, os BCF e os pontos de vista. Quem a receber abre tudo com uma ligação.'
                : 'Abra modelos (ou um BCF) para ter uma sessão a partilhar.'
            }
            onClick={abrir}
          >
            <Share2 size={14} />
            Partilhar sessão…
          </button>
        )
      )}
      {lista.length > 0 && (
        <div className="px-2 pb-1 pt-0.5">
          <p className="pb-0.5 text-[10px] uppercase tracking-wide text-[var(--color-text-dim)]">
            Sessões partilhadas
          </p>
          <ul className="flex flex-col gap-0.5">
            {lista.map((s) => (
              <li key={s.id} className="flex items-center gap-1 text-[11px]">
                <span
                  className="min-w-0 flex-1 truncate text-[var(--color-text-muted)]"
                  title={`${s.nome ?? 'Sessão'}${s.dono_nome ? `, de ${s.dono_nome}` : ''}`}
                >
                  {s.nome ?? 'Sessão'}
                </span>
                <button
                  type="button"
                  className={BOTAO_PEQUENO}
                  title="Abrir esta sessão (fecha a atual, se houver)."
                  onClick={() => {
                    close()
                    navigate(`/${workspace.slug}/?sessao=${encodeURIComponent(s.id)}`)
                  }}
                >
                  Abrir
                </button>
                <button
                  type="button"
                  className={BOTAO_PEQUENO}
                  title={s.pode_gerir ? 'Com quem está partilhada, ou retirar a partilha.' : 'Com quem está partilhada.'}
                  onClick={() => {
                    close()
                    abrirJanelaPartilhar({
                      tipo: 'sessao',
                      nome: s.nome ?? 'Sessão',
                      partilhaId: s.id,
                      workspaceNome: workspace.name,
                    })
                  }}
                >
                  {s.pode_gerir ? 'Gerir' : 'Acessos'}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  )
}
